import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from '@tanstack/react-table';
import { getActiveTradeGroupsDetails, TradeGroup, getBollingerEntryConditions, getRsiExtremesEntryConditions, getMacdEntryConditions } from '../../api/tradeGroups';
import BollingerEntryConditionsBar from '../indicators/BollingerEntryConditionsBar';
import BollingerExitConditionsBar from '../indicators/BollingerExitConditionsBar';
import RsiExtremesEntryConditionsBar from '../indicators/RsiExtremesEntryConditionsBar';
import RsiExtremesExitConditionsBar from '../indicators/RsiExtremesExitConditionsBar';
import MacdEntryConditionsBar from '../indicators/MacdEntryConditionsBar';
import MacdExitConditionsBar from '../indicators/MacdExitConditionsBar';
import VwapEntryConditionsBar from '../indicators/VwapEntryConditionsBar';
import VwapExitConditionsBar from '../indicators/VwapExitConditionsBar';
import IchimokuEntryConditionsBar from '../indicators/IchimokuEntryConditionsBar';
import IchimokuExitConditionsBar from '../indicators/IchimokuExitConditionsBar';
import './ActiveTradeGroupsTable.css';

/**
 * Format currency with $ sign and appropriate decimal places
 * Uses 4 decimals for values < $1 (e.g., Cardano), 2 decimals otherwise
 */
const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '$0.00';

  // Use 4 decimal places for values under $1 (low-value assets like Cardano)
  const decimalPlaces = Math.abs(value) < 1 ? 4 : 2;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);
};

/**
 * Format relative time
 */
const formatRelativeTime = (timestamp: number | null): string => {
  if (!timestamp) return 'N/A';
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

/**
 * Trade Group Card Component (Mobile-friendly)
 * Renders different layouts based on trade group type
 */
const TradeGroupCard: React.FC<{ 
  tradeGroup: TradeGroup;
}> = ({ tradeGroup }) => {
  const navigate = useNavigate();
  const combinedPnlColor = tradeGroup.combinedPnl >= 0 ? 'positive' : 'negative';
  const unrealizedPnlColor = tradeGroup.openSellUnrealizedProfit >= 0 ? 'positive' : 'negative';
  const returnColor = tradeGroup.avgCapturedReturn >= 0 ? 'positive' : 'negative';
  
  // Fetch entry conditions for Bollinger Reversion trade groups
  const isBollingerReversion = tradeGroup.tradeGroupType === 'BOLLINGER_REVERSION';
  const isRsiExtremes = tradeGroup.tradeGroupType === 'RSI_EXTREMES';
  const isMacdReversion = tradeGroup.tradeGroupType === 'MACD_REVERSION';
  const isVwapReversion = tradeGroup.tradeGroupType === 'VWAP_REVERSION';
  const isIchimokuCloud = tradeGroup.tradeGroupType === 'ICHIMOKU_CLOUD';

  const { data: bollingerEntryConditions, isLoading: isBollingerLoading, isError: isBollingerError } = useQuery({
    queryKey: ['bollinger-entry-conditions', tradeGroup.tradeGroupId],
    queryFn: () => getBollingerEntryConditions(tradeGroup.tradeGroupId),
    enabled: isBollingerReversion && tradeGroup.isActive,
    staleTime: 30000, // 30 seconds cache
    retry: 1,
  });

  const { data: rsiEntryConditions, isLoading: isRsiLoading, isError: isRsiError } = useQuery({
    queryKey: ['rsi-entry-conditions', tradeGroup.tradeGroupId],
    queryFn: () => getRsiExtremesEntryConditions(tradeGroup.tradeGroupId),
    enabled: isRsiExtremes && tradeGroup.isActive,
    staleTime: 30000, // 30 seconds cache
    retry: 1,
  });

  const { data: macdEntryConditions, isLoading: isMacdLoading, isError: isMacdError } = useQuery({
    queryKey: ['macd-entry-conditions', tradeGroup.tradeGroupId],
    queryFn: () => getMacdEntryConditions(tradeGroup.tradeGroupId),
    enabled: isMacdReversion && tradeGroup.isActive,
    staleTime: 30000,
    retry: 1,
  });

  const handleClick = () => {
    navigate(`/trade-groups/${tradeGroup.tradeGroupId}`);
  };

  // Handle null state
  const state = tradeGroup.state || 'UNKNOWN';
  const stateClass = state.toLowerCase().replace('_', '-');
  const stateDisplay = state.replace('_', ' ');

  // Format price status for display
  const priceStatus = tradeGroup.currentPriceStatus || 'UNKNOWN';
  const priceStatusClass = priceStatus.toLowerCase().replace('_', '-');
  const priceStatusDisplay = priceStatus.replace('_', ' ');

  const current = tradeGroup.currentPrice;
  const min = tradeGroup.minBuyPrice;
  const max = tradeGroup.maxBuyPrice;
  const isBelow = current < min;
  const isAbove = current > max;

  // Calculate price position for visual indicator
  const getMarkerStyle = () => {
    if (current < min) {
      // Below range - calculate distance for visual gap
        // Position marker to left of bar start, with visual gap
        return { left: `calc(0% - 30px)` };
    } else if (current > max) {
        // Position to the right of the bar with visual gap
        return { left: `calc(100% + 10px)` };
    } else {
      // Within range
        const pricePosition = ((current - min) / (max - min)) * 100;
        return { left: `${pricePosition}%` };
    }
  };

  // Check if this is a Future Ladder trade group with minimal activity
  const isFutureLadder = tradeGroup.tradeGroupType === 'FUTURE_LADDER';
  // const isRegularLadder = tradeGroup.tradeGroupType === 'REGULAR_LADDER';
  const isMomentumSwing = tradeGroup.tradeGroupType === 'MOMENTUM_SWING';
  const isVolatilityLadder = tradeGroup.tradeGroupType === 'VOLATILITY_LADDER';
  // const hasMinimalActivity = tradeGroup.openSellCostBasis === 0 && tradeGroup.openBuyCostBasis === 0;
  const showAllocatedCapital = isFutureLadder && tradeGroup.originalQuoteSize && tradeGroup.state === 'ACTIVE';

  // Get card type class for styling
  const getCardTypeClass = () => {
    if (isFutureLadder) return 'future-ladder-card';
    if (isMomentumSwing) return 'momentum-swing-card';
    if (isVolatilityLadder) return 'volatility-ladder-card';
    if (isBollingerReversion) return 'bollinger-reversion-card';
    if (isRsiExtremes) return 'rsi-extremes-card';
    if (isMacdReversion) return 'macd-reversion-card';
    if (isVwapReversion) return 'vwap-reversion-card';
    if (isIchimokuCloud) return 'ichimoku-cloud-card';
    return 'regular-ladder-card';
  };

  return (
    <div 
      className={`trade-group-card ${getCardTypeClass()}`} 
      onClick={handleClick} 
      role="button" 
      tabIndex={0}
    >
      <div className="card-header-row">
        <div className="card-title">
          <h3>{tradeGroup.name}</h3>
          <span className="product-id">{tradeGroup.productId}</span>
        </div>
        <div className="card-header-badges">
          <span className={`status-badge status-${stateClass}`}>
            {stateDisplay}
          </span>
           <span className={`price-status-badge price-status-${priceStatusClass}`}>
            {priceStatusDisplay}
          </span>
          {/* Pause indicator badge (RSI-PAUSE-009) */}
          {(tradeGroup.state === 'PAUSED' || tradeGroup.state === 'OPERATOR_PAUSED') && tradeGroup.lastPauseTime && (
            <span
              className="pause-indicator-badge"
              title={tradeGroup.lastPauseReason || 'Trade group is paused'}
              aria-label={`Paused ${formatRelativeTime(tradeGroup.lastPauseTime)}: ${tradeGroup.lastPauseReason || 'No reason provided'}`}
            >
              ⏸️ {formatRelativeTime(tradeGroup.lastPauseTime)}
            </span>
          )}
        </div>
      </div>

      {/* Conditional: Show Bollinger indicators OR RSI Extremes indicators OR Price Range */}
      {isBollingerReversion && tradeGroup.state === 'WATCHING' ? (
        /* Bollinger Reversion Entry Indicators Section - Only when WATCHING */
        <div className="bollinger-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>

          {/* Entry Conditions Bar - Replaces the 4-indicator grid */}
          <BollingerEntryConditionsBar
            entryConditions={bollingerEntryConditions?.entry_conditions}
            isLoading={isBollingerLoading}
            isError={isBollingerError}
          />
        </div>
      ) : isBollingerReversion && (tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') ? (
        /* Bollinger Reversion Exit Indicators Section - Only when IN_POSITION or PARTIAL_EXIT */
        <div className="bollinger-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>

          {/* Exit Conditions Bar - Shows exit targets */}
          <BollingerExitConditionsBar
            exitConditions={{
              currentPrice: tradeGroup.currentPrice,
              stopLoss: tradeGroup.bollingerStopLoss,
              middleBand: tradeGroup.bollingerMiddleBand,
              upperBand: tradeGroup.bollingerUpperBand,
              isPartialExit: tradeGroup.state === 'PARTIAL_EXIT',
            }}
          />
        </div>
      ) : isRsiExtremes && tradeGroup.state === 'WATCHING' ? (
        /* RSI Extremes Entry Indicators Section - Only when WATCHING */
        <div className="rsi-extremes-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>

          {/* RSI Entry Conditions Bar */}
          <RsiExtremesEntryConditionsBar
            entryConditions={rsiEntryConditions?.entry_conditions}
            isLoading={isRsiLoading}
            isError={isRsiError}
          />
        </div>
      ) : isRsiExtremes && (tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') ? (
        /* RSI Extremes Exit Indicators Section - Only when IN_POSITION or PARTIAL_EXIT */
        <div className="rsi-extremes-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>

          {/* RSI Exit Conditions Bar */}
          <RsiExtremesExitConditionsBar
            exitConditions={{
              currentPrice: tradeGroup.currentPrice,
              currentRsi: rsiEntryConditions?.rsi ?? undefined,
              stopLoss: tradeGroup.rsiStopLoss,
              rsiOverboughtThreshold: tradeGroup.rsiOverboughtThreshold || 70,
              profitTarget: tradeGroup.rsiProfitTarget,
              trailingStopActivation: tradeGroup.rsiTrailingStopActivationPrice,
              trailingStopPrice: tradeGroup.rsiTrailingStopDistance ? tradeGroup.currentPrice * (1 - tradeGroup.rsiTrailingStopDistance / 100) : undefined,
              isPartialExit: tradeGroup.state === 'PARTIAL_EXIT',
            }}
            isLoading={isRsiLoading}
          />
        </div>
      ) : isMacdReversion && tradeGroup.state === 'WATCHING' ? (
        /* MACD Reversion Entry Indicators Section - Only when WATCHING */
        <div className="macd-indicators-section">
          <MacdEntryConditionsBar
            entryConditions={macdEntryConditions?.entry_conditions}
            isLoading={isMacdLoading}
            isError={isMacdError}
          />
        </div>
      ) : isMacdReversion && tradeGroup.state === 'IN_POSITION' ? (
        /* MACD Reversion Exit Indicators Section */
        <div className="macd-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>
          <MacdExitConditionsBar
            exitConditions={{
              currentPrice: tradeGroup.currentPrice,
              stopLoss: tradeGroup.macdStopLoss,
              profitTarget: tradeGroup.macdProfitTarget,
              entryPrice: tradeGroup.macdEntryPrice,
            }}
            isLoading={isMacdLoading}
          />
        </div>
      ) : isVwapReversion && tradeGroup.state === 'WATCHING' ? (
        /* VWAP Reversion Entry Indicators Section — VwapEntryConditionsBar is
           self-fetching (TASK-040), so it owns its own data hook. */
        <div className="vwap-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>
          <VwapEntryConditionsBar tradeGroupId={tradeGroup.tradeGroupId} />
        </div>
      ) : isVwapReversion && (tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') ? (
        /* VWAP Reversion Exit Indicators Section — uses VwapExitConditionsBar
           matching the visual pattern of sibling exit bars. */
        <div className="vwap-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>
          <VwapExitConditionsBar
            exitConditions={{
              currentPrice: tradeGroup.currentPrice,
              stopLoss: tradeGroup.vwapStopLoss,
              stopLossLimit: tradeGroup.vwapStopLossLimit,
              entryPrice: tradeGroup.vwapEntryPrice,
              vwapTarget: tradeGroup.vwapEntryVwap,
              isPartialExit: tradeGroup.state === 'PARTIAL_EXIT',
            }}
          />
        </div>
      ) : isIchimokuCloud && tradeGroup.state === 'WATCHING' ? (
        /* Ichimoku Cloud Entry Indicators — self-fetching bar per PLAN-044. */
        <div className="ichimoku-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>
          <IchimokuEntryConditionsBar tradeGroupId={tradeGroup.tradeGroupId} />
        </div>
      ) : isIchimokuCloud && (tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') ? (
        /* Ichimoku Cloud Exit Indicators — shares queryKey with entry bar (PLAN-045). */
        <div className="ichimoku-indicators-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className="current-price-large">
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>
          <IchimokuExitConditionsBar tradeGroupId={tradeGroup.tradeGroupId} />
        </div>
      ) : !isBollingerReversion && !isRsiExtremes && !isMacdReversion && !isVwapReversion && !isIchimokuCloud ? (
        /* Traditional Price Position Indicator for other trade group types */
        <div className="price-position-section">
          <div className="price-position-header">
            <span className="price-position-label">Current Price</span>
            <span className={`current-price-large ${isBelow ? 'below-range' : isAbove ? 'above-range' : 'in-range'}`}>
              {formatCurrency(tradeGroup.currentPrice)}
            </span>
          </div>

          <div className="price-range-visualizer">
            <div className="price-range-track">
              <div className="price-range-bar"></div>
              <div
                className={`price-position-marker ${isBelow ? 'below' : isAbove ? 'above' : 'within'}`}
                style={getMarkerStyle()}
              >
                <div className="marker-dot"></div>
              </div>
            </div>
            <div className="price-range-labels">
              <div className="price-range-label-item">
                <span className="label-text">Min</span>
                <span className="label-value">{formatCurrency(tradeGroup.minBuyPrice)}</span>
              </div>
              <div className="price-range-label-item price-range-center">
                <span className="label-text">Buy Range</span>
              </div>
              <div className="price-range-label-item">
                <span className="label-text">Max</span>
                <span className="label-value">{formatCurrency(tradeGroup.maxBuyPrice)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/*/!* Volatility Information Section - Only for VOLATILITY_LADDER *!/*/}
      {/*{isVolatilityLadder && (*/}
      {/*  <div className="volatility-info-section">*/}
      {/*    <div className="volatility-header">*/}
      {/*      <span className="volatility-label">Volatility Status</span>*/}
      {/*      <span */}
      {/*        className="volatility-regime-badge"*/}
      {/*        style={{ */}
      {/*          backgroundColor: getVolatilityRegimeColor(tradeGroup.volatilityRegime),*/}
      {/*          color: '#fff',*/}
      {/*          padding: '4px 12px',*/}
      {/*          borderRadius: '12px',*/}
      {/*          fontSize: '12px',*/}
      {/*          fontWeight: 'bold'*/}
      {/*        }}*/}
      {/*      >*/}
      {/*        {tradeGroup.volatilityRegime || 'UNKNOWN'}*/}
      {/*      </span>*/}
      {/*    </div>*/}
      {/*    <div className="volatility-metrics-row">*/}
      {/*      <div className="volatility-metric">*/}
      {/*        <span className="volatility-metric-label">Position Multiplier</span>*/}
      {/*        <span className="volatility-metric-value">*/}
      {/*          {tradeGroup.currentPositionMultiplier */}
      {/*            ? `${tradeGroup.currentPositionMultiplier.toFixed(2)}x`*/}
      {/*            : 'N/A'}*/}
      {/*        </span>*/}
      {/*      </div>*/}
      {/*      <div className="volatility-metric">*/}
      {/*        <span className="volatility-metric-label">Capital Deployed</span>*/}
      {/*        <div className="capital-progress-container">*/}
      {/*          <div className="capital-progress-bar">*/}
      {/*            <div */}
      {/*              className="capital-progress-fill"*/}
      {/*              style={{ */}
      {/*                width: `${(tradeGroup.capitalDeployedPct || 0) * 100}%`,*/}
      {/*                backgroundColor: '#4CAF50'*/}
      {/*              }}*/}
      {/*            />*/}
      {/*          </div>*/}
      {/*          <span className="capital-progress-text">*/}
      {/*            {((tradeGroup.capitalDeployedPct || 0) * 100).toFixed(0)}%*/}
      {/*          </span>*/}
      {/*        </div>*/}
      {/*      </div>*/}
      {/*      <div className="volatility-metric">*/}
      {/*        <span className="volatility-metric-label">Period</span>*/}
      {/*        <span className="volatility-metric-value">*/}
      {/*          {formatVolatilityPeriod(tradeGroup.volatilityPeriod)}*/}
      {/*        </span>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*    {tradeGroup.lastRebalanceTime && (*/}
      {/*      <div className="volatility-footer">*/}
      {/*        <span className="volatility-footer-text">*/}
      {/*          🔄 Last rebalance: {formatRelativeTime(tradeGroup.lastRebalanceTime)}*/}
      {/*        </span>*/}
      {/*      </div>*/}
      {/*    )}*/}
      {/*  </div>*/}
      {/*)}*/}

      {/* Conditional rendering based on trade group type and activity */}
      {isBollingerReversion ? (
        /* Bollinger Reversion - simplified metrics */
        <div className="card-metrics">
          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cumulative P&L</span>
              <span className={`metric-value ${(tradeGroup.bollingerCumulativePnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(tradeGroup.bollingerCumulativePnl || 0)}
              </span>
              <span className="metric-sub">All cycles</span>
            </div>
            <div className="metric">
              <span className="metric-label">Current Cycle P&L</span>
              <span className={`metric-value ${combinedPnlColor}`}>
                {formatCurrency(tradeGroup.combinedPnl)}
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cycles</span>
              <span className="metric-value-medium">
                {tradeGroup.bollingerCycleCount || 0}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Win Rate</span>
              <span className="metric-value-medium">
                {tradeGroup.bollingerWinRate != null
                  ? (tradeGroup.bollingerWinRate * 100).toFixed(1)
                  : '0.0'}%
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Entry Price</span>
              <span className="metric-value-medium">
                {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && tradeGroup.bollingerEntryPrice 
                  ? formatCurrency(tradeGroup.bollingerEntryPrice) 
                  : tradeGroup.state === 'WATCHING' ? '—' : 'N/A'}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Time in Position</span>
              <span className="metric-value-medium">
                {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && tradeGroup.bollingerEntryTime
                  ? `${Math.floor((Date.now() - tradeGroup.bollingerEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.bollingerEntryTime) % 3600000) / 60000)}m`
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      ) : isRsiExtremes ? (
        /* RSI Extremes - metrics matching Bollinger pattern */
        <div className="card-metrics">
          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cumulative P&L</span>
              <span className={`metric-value ${(tradeGroup.rsiCumulativePnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(tradeGroup.rsiCumulativePnl || 0)}
              </span>
              <span className="metric-sub">All cycles</span>
            </div>
            <div className="metric">
              <span className="metric-label">Current Cycle P&L</span>
              <span className={`metric-value ${combinedPnlColor}`}>
                {formatCurrency(tradeGroup.combinedPnl)}
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cycles</span>
              <span className="metric-value-medium">
                {tradeGroup.rsiCycleCount || 0}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Win Rate</span>
              <span className="metric-value-medium">
                {tradeGroup.rsiWinRate != null
                  ? (tradeGroup.rsiWinRate * 100).toFixed(1)
                  : '0.0'}%
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Entry Price</span>
              <span className="metric-value-medium">
                {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && tradeGroup.rsiEntryPrice
                  ? formatCurrency(tradeGroup.rsiEntryPrice)
                  : tradeGroup.state === 'WATCHING' ? '—' : 'N/A'}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Time in Position</span>
              <span className="metric-value-medium">
                {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && tradeGroup.rsiEntryTime
                  ? `${Math.floor((Date.now() - tradeGroup.rsiEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.rsiEntryTime) % 3600000) / 60000)}m`
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      ) : isVwapReversion ? (
        /* VWAP Reversion - metrics matching sibling pattern */
        <div className="card-metrics">
          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cumulative P&L</span>
              <span className={`metric-value ${(tradeGroup.vwapCumulativePnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(tradeGroup.vwapCumulativePnl || 0)}
              </span>
              <span className="metric-sub">All cycles</span>
            </div>
            <div className="metric">
              <span className="metric-label">Current Cycle P&L</span>
              <span className={`metric-value ${combinedPnlColor}`}>
                {formatCurrency(tradeGroup.combinedPnl)}
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cycles</span>
              <span className="metric-value-medium">
                {tradeGroup.vwapCycleCount || 0}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Win Rate</span>
              <span className="metric-value-medium">
                {tradeGroup.vwapWinRate != null
                  ? (tradeGroup.vwapWinRate * 100).toFixed(1)
                  : '0.0'}%
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Entry Price</span>
              <span className="metric-value-medium">
                {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && tradeGroup.vwapEntryPrice
                  ? formatCurrency(tradeGroup.vwapEntryPrice)
                  : tradeGroup.state === 'WATCHING' ? '—' : 'N/A'}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Time in Position</span>
              <span className="metric-value-medium">
                {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && tradeGroup.vwapEntryTime
                  ? `${Math.floor((Date.now() - tradeGroup.vwapEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.vwapEntryTime) % 3600000) / 60000)}m`
                  : '—'}
              </span>
            </div>
          </div>
        </div>
      ) : isMacdReversion ? (
        /* MACD Reversion - metrics */
        <div className="card-metrics">
          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cumulative P&L</span>
              <span className={`metric-value ${(tradeGroup.macdCumulativePnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(tradeGroup.macdCumulativePnl || 0)}
              </span>
              <span className="metric-sub">All cycles</span>
            </div>
            <div className="metric">
              <span className="metric-label">Current Cycle P&L</span>
              <span className={`metric-value ${combinedPnlColor}`}>
                {formatCurrency(tradeGroup.combinedPnl)}
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Cycles</span>
              <span className="metric-value-medium">
                {tradeGroup.macdCycleCount || 0}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Win Rate</span>
              <span className="metric-value-medium">
                {tradeGroup.macdWinRate != null
                  ? (tradeGroup.macdWinRate * 100).toFixed(1)
                  : '0.0'}%
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Entry Price</span>
              <span className="metric-value-medium">
                {tradeGroup.state === 'IN_POSITION' && tradeGroup.macdEntryPrice
                  ? formatCurrency(tradeGroup.macdEntryPrice)
                  : tradeGroup.state === 'WATCHING' ? '\u2014' : 'N/A'}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Time in Position</span>
              <span className="metric-value-medium">
                {tradeGroup.state === 'IN_POSITION' && tradeGroup.macdEntryTime
                  ? `${Math.floor((Date.now() - tradeGroup.macdEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.macdEntryTime) % 3600000) / 60000)}m`
                  : '\u2014'}
              </span>
            </div>
          </div>
        </div>
      ) : showAllocatedCapital || isVolatilityLadder ? (
        /* Future Ladder with no activity - show allocated capital instead of open costs */
        <div className="card-metrics">
          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Overall P&L</span>
              <span className={`metric-value ${combinedPnlColor}`}>
                {formatCurrency(tradeGroup.combinedPnl)}
              </span>
              <span className="metric-sub">Total Profit/Loss</span>
            </div>
            <div className="metric">
              <span className="metric-label">Unrealized P&L</span>
              <span className={`metric-value ${unrealizedPnlColor}`}>
                {formatCurrency(tradeGroup.openSellUnrealizedProfit)}
              </span>
              <span className="metric-sub">Open Orders</span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label allocated-cap-label">Allocated Cap</span>
              <span className="metric-value-medium allocated-cap-value">{formatCurrency(tradeGroup.originalQuoteSize!)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Percent Deployed</span>
              <span className="metric-value-medium">
                {tradeGroup.originalQuoteSize && tradeGroup.originalQuoteSize > 0
                  ? (((tradeGroup.openBuyCostBasis + tradeGroup.openSellCostBasis) / tradeGroup.originalQuoteSize) * 100).toFixed(1)
                  : '0.0'}%
              </span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Avg Return</span>
              <span className={`metric-value-medium ${returnColor}`}>
                {(tradeGroup.avgCapturedReturn * 100).toFixed(2)}%
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Keep Alive</span>
              <div className="keep-alive-count-badge-container">
                <span className="keep-alive-count-badge">{tradeGroup.keepAliveCount || 0}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Regular display for active trades or Regular Ladder */
        <div className="card-metrics">
          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Overall P&L</span>
              <span className={`metric-value ${combinedPnlColor}`}>
                {formatCurrency(tradeGroup.combinedPnl)}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Unrealized P&L</span>
              <span className={`metric-value ${unrealizedPnlColor}`}>
                {formatCurrency(tradeGroup.openSellUnrealizedProfit)}
              </span>
            </div>
          </div>

          <div className="metric-row">
              <div className="metric">
                  <span className="metric-label">Open Buy Cost</span>
                  <span className="metric-value-medium">{formatCurrency(tradeGroup.openBuyCostBasis)}</span>
              </div>
            <div className="metric">
              <span className="metric-label">Open Sell Cost</span>
              <span className="metric-value-medium">{formatCurrency(tradeGroup.openSellCostBasis)}</span>
            </div>
          </div>

          <div className="metric-row">
            <div className="metric">
              <span className="metric-label">Avg Return</span>
              <span className={`metric-value-medium ${returnColor}`}>
                {(tradeGroup.avgCapturedReturn * 100).toFixed(2)}%
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Keep Alive</span>
              <div className="keep-alive-count-badge-container">
                <span className="keep-alive-count-badge">{tradeGroup.keepAliveCount || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card-footer">
        <span className="last-activity">
          🕒 {formatRelativeTime(tradeGroup.lastModified)}
        </span>
      </div>
    </div>
  );
};

/**
 * Multi-Select Dropdown Component
 */
interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ label, options, selected, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(s => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  const selectAll = () => {
    onChange(options);
  };

  const clearAll = () => {
    onChange([]);
  };

  return (
    <div className="multi-select">
      <label>{label}</label>
      <div className="multi-select-dropdown">
        <button
          className="multi-select-button"
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          {selected.length === 0 ? placeholder : `${selected.length} selected`}
          <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
        </button>
        {isOpen && (
          <div className="multi-select-options">
            <div className="multi-select-actions">
              <button onClick={selectAll} type="button">Select All</button>
              <button onClick={clearAll} type="button">Clear</button>
            </div>
            {options.filter(option => option != null).map(option => (
              <label key={option} className="multi-select-option">
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleOption(option)}
                />
                <span>{option.replace('_', ' ')}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Active Trade Groups Table Component
 */
interface ActiveTradeGroupsTableProps {
  selectedTypeFilter?: string[];
  onTypeFilterChange?: (types: string[]) => void;
}

const ActiveTradeGroupsTable: React.FC<ActiveTradeGroupsTableProps> = ({
  selectedTypeFilter = [],
  onTypeFilterChange
}) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

  // Filter states - default to showing active trade groups
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(true);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(selectedTypeFilter);
  const [searchText, setSearchText] = useState('');

  // Table sorting state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'lastModified', desc: true }]);

  // Available filter options - all possible states
  const statusOptions = ['ACTIVE', 'LOSS_PROTECTION', 'PROFIT_PROTECTION', 'FORCE_EXIT', 'CANCELLED', 'INACTIVE', 'WATCHING', 'IN_POSITION', 'PARTIAL_EXIT', 'PAUSED', 'OPERATOR_PAUSED'];

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['active-trade-groups-details'],
    queryFn: getActiveTradeGroupsDetails,
    retry: 2,
  });

  // Extract unique products and types from data
  const productOptions = useMemo(() => {
    if (!data?.tradeGroups) return [];
    const products = new Set(data.tradeGroups.map(tg => tg.productId));
    return Array.from(products).sort();
  }, [data]);

  const typeOptions = useMemo(() => {
    if (!data?.tradeGroups) return [];
    const types = new Set(data.tradeGroups.map(tg => tg.tradeGroupType).filter(t => t));
    return Array.from(types).sort();
  }, [data]);

  // Sync external type filter with internal state
  React.useEffect(() => {
    setSelectedTypes(selectedTypeFilter);
  }, [selectedTypeFilter]);

  // Notify parent when type filter changes
  const handleTypeChange = (types: string[]) => {
    setSelectedTypes(types);
    if (onTypeFilterChange) {
      onTypeFilterChange(types);
    }
  };

  // Filter and sort trade groups based on selected filters and search
  const filteredTradeGroups = useMemo(() => {
    if (!data?.tradeGroups) return [];

    const filtered = data.tradeGroups.filter(tg => {
      // Active filter - uses the isActive field from the database
      if (showActiveOnly && !tg.isActive) {
        return false;
      }

      // Status filter (optional additional filtering by specific states)
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(tg.state)) {
        return false;
      }

      // Product filter
      if (selectedProducts.length > 0 && !selectedProducts.includes(tg.productId)) {
        return false;
      }

      // Type filter
      if (selectedTypes.length > 0 && !selectedTypes.includes(tg.tradeGroupType)) {
        return false;
      }

      // Search filter (searches name, ID, and product)
      if (searchText.trim()) {
        const search = searchText.toLowerCase();
        return (
          tg.name.toLowerCase().includes(search) ||
          tg.tradeGroupId.toLowerCase().includes(search) ||
          tg.productId.toLowerCase().includes(search)
        );
      }

      return true;
    });

    // Sort: lastModified desc (nulls last), then type asc, then name asc
    return filtered.sort((a, b) => {
      // Handle null lastModified - nulls go to the end
      const aTime = a.lastModified ?? 0;
      const bTime = b.lastModified ?? 0;

      if (aTime === 0 && bTime === 0) {
        // Both null - sort by type, then name
        const typeCompare = (a.tradeGroupType || '').localeCompare(b.tradeGroupType || '');
        if (typeCompare !== 0) return typeCompare;
        return a.name.localeCompare(b.name);
      }
      if (aTime === 0) return 1;  // a is null, goes after b
      if (bTime === 0) return -1; // b is null, goes after a

      // Both have timestamps - sort by lastModified desc (most recent first)
      const timeDiff = bTime - aTime;
      if (timeDiff !== 0) return timeDiff;

      // Same timestamp - sort by type, then name
      const typeCompare = (a.tradeGroupType || '').localeCompare(b.tradeGroupType || '');
      if (typeCompare !== 0) return typeCompare;

      return a.name.localeCompare(b.name);
    });
  }, [data, showActiveOnly, selectedStatuses, selectedProducts, selectedTypes, searchText]);

  // Table columns definition
  const columns = useMemo<ColumnDef<TradeGroup>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Trade Group',
        cell: (info) => {
          const tg = info.row.original;
          return (
            <div className="table-cell-title" onClick={() => navigate(`/trade-groups/${tg.tradeGroupId}`)} style={{ cursor: 'pointer' }}>
              <strong>{tg.name}</strong>
              <span>{tg.productId}</span>
            </div>
          );
        },
      },

      {
        accessorKey: 'state',
        header: 'Status',
        cell: (info) => {
          const state = (info.getValue() as string) || 'UNKNOWN';
          return (
            <span className={`status-badge status-${state.toLowerCase().replace('_', '-')}`}>
              {state.replace('_', ' ')}
            </span>
          );
        },
      },

      {
        accessorKey: 'keepAliveCount',
        header: 'Keep Alive',
        cell: (info) => {
          const count = (info.getValue() as number) || 0;
          return (
            <span className="keep-alive-count-badge">
              {count}
            </span>
          );
        },
      },
      {
        accessorKey: 'combinedPnl',
        header: 'Overall P&L',
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <span className={value >= 0 ? 'positive' : 'negative'}>
              {formatCurrency(value)}
            </span>
          );
        },
      },
      {
        accessorKey: 'openSellUnrealizedProfit',
        header: 'Unrealized P&L',
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <span className={value >= 0 ? 'positive' : 'negative'}>
              {formatCurrency(value)}
            </span>
          );
        },
      },
      {
        accessorKey: 'totalCapturedProfit',
        header: 'Realized Profit',
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <span className={value >= 0 ? 'positive' : 'negative'}>
              {formatCurrency(value)}
            </span>
          );
        },
      },
      {
        accessorKey: 'avgCapturedReturn',
        header: 'Avg Return %',
        cell: (info) => {
          const value = info.getValue() as number;
          return (
            <span className={value >= 0 ? 'positive' : 'negative'}>
              {(value * 100).toFixed(2)}%
            </span>
          );
        },
      },
        {
            accessorKey: 'openBuyCostBasis',
            header: 'Buy Cost',
            cell: (info) => formatCurrency(info.getValue() as number),
        },
      {
        accessorKey: 'openSellCostBasis',
        header: 'Sell Cost',
        cell: (info) => formatCurrency(info.getValue() as number),
      },
      {
        id: 'orders',
        header: 'Orders',
        accessorFn: (row) => row.openBuyOrders + row.openSellOrders,
        cell: (info) => {
          const tg = info.row.original;
          return (
            <div className="orders-badges">
              <span className="order-badge buy-badge">{tg.openBuyOrders}</span>
              <span className="order-badge sell-badge">{tg.openSellOrders}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'lastModified',
        header: 'Last Activity',
        cell: (info) => formatRelativeTime(info.getValue() as number),
      },
    ],
    [navigate]
  );

  // Create table instance
  const table = useReactTable({
    data: filteredTradeGroups,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (isLoading) {
    return (
      <div className="trade-groups-container">
        <div className="container-header">
          <h2>Trade Groups</h2>
          <div className="loading-indicator">Loading...</div>
        </div>
        <div className="cards-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="trade-group-card skeleton-card">
              <div className="skeleton-line" style={{ width: '60%', height: '20px' }}></div>
              <div className="skeleton-line" style={{ width: '100%', height: '60px', marginTop: '1rem' }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="trade-groups-container error-container">
        <div className="container-header">
          <h2>Trade Groups</h2>
        </div>
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <p>Unable to load trade groups</p>
          <p className="error-detail">
            {error instanceof Error ? error.message : 'Please try again later'}
          </p>
        </div>
      </div>
    );
  }

  if (!data || data.count === 0) {
    return (
      <div className="trade-groups-container">
        <div className="container-header">
          <h2>Trade Groups</h2>
        </div>
        <div className="empty-state">
          <span className="empty-icon">📊</span>
          <p>No trade groups found</p>
          <p className="empty-detail">Trade groups will appear here when they are created</p>
        </div>
      </div>
    );
  }

  const totalCombinedPnL = filteredTradeGroups.reduce((sum, tg) => sum + tg.combinedPnl, 0);
  const totalUnrealizedPnL = filteredTradeGroups.reduce((sum, tg) => sum + tg.openSellUnrealizedProfit, 0);
  const totalRealized = filteredTradeGroups.reduce((sum, tg) => sum + tg.totalCapturedProfit, 0);
  const totalCostBasis = filteredTradeGroups.reduce((sum, tg) => sum + tg.openSellCostBasis, 0);
  const totalFees = filteredTradeGroups.reduce((sum, tg) => sum + tg.totalFees, 0);
  // RSI-PAUSE-009: Count paused trade groups (includes operator-initiated pauses)
  const pausedCount = filteredTradeGroups.filter(tg => tg.state === 'PAUSED' || tg.state === 'OPERATOR_PAUSED').length;

  return (
    <div className="trade-groups-container">
      <div className="container-header">
        <div className="header-left">
          <h2>Trade Groups</h2>
          <span className="count-badge">{filteredTradeGroups.length} of {data.count}</span>
        </div>
        <div className="header-right">
          <div className="view-toggle">
            <button
              className={viewMode === 'cards' ? 'active' : ''}
              onClick={() => setViewMode('cards')}
              title="Card View"
            >
              ⊞
            </button>
            <button
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="active-toggle" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#e2e8f0', fontSize: '14px' }}>
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            Active Only
          </label>
        </div>

        <MultiSelect
          label="Status"
          options={statusOptions}
          selected={selectedStatuses}
          onChange={setSelectedStatuses}
          placeholder="All statuses"
        />

        <MultiSelect
          label="Product"
          options={productOptions}
          selected={selectedProducts}
          onChange={setSelectedProducts}
          placeholder="All products"
        />

        <MultiSelect
          label="Type"
          options={typeOptions}
          selected={selectedTypes}
          onChange={handleTypeChange}
          placeholder="All types"
        />

        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, ID, or product..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="search-input"
          />
          {searchText && (
            <button
              className="clear-search"
              onClick={() => setSearchText('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'cards' ? (
        filteredTradeGroups.length > 0 ? (
          <div className="cards-grid">
            {filteredTradeGroups.map((tradeGroup) => (
              <TradeGroupCard 
                key={tradeGroup.tradeGroupId} 
                tradeGroup={tradeGroup}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-icon">🔍</span>
            <p>No trade groups match your filters</p>
            <p className="empty-detail">Try adjusting your search or filter criteria</p>
          </div>
        )
      ) : (
        filteredTradeGroups.length > 0 ? (
          <div className="table-container">
            <table className="trade-groups-table">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className={header.column.getCanSort() ? 'sortable' : ''}
                        style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                      >
                        <div className="th-content">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="sort-indicator">
                              {{
                                asc: ' ↑',
                                desc: ' ↓',
                              }[header.column.getIsSorted() as string] ?? ' ⇅'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-icon">🔍</span>
            <p>No trade groups match your filters</p>
            <p className="empty-detail">Try adjusting your search or filter criteria</p>
          </div>
        )
      )}

      {/* Summary Footer */}
      {filteredTradeGroups.length > 0 && (
        <div className="summary-footer">
          <div className="summary-item">
            <span className="summary-label">Overall P&L</span>
            <span className={`summary-value ${totalCombinedPnL >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(totalCombinedPnL)}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Unrealized P&L</span>
            <span className={`summary-value ${totalUnrealizedPnL >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(totalUnrealizedPnL)}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Realized Profit</span>
            <span className={`summary-value ${totalRealized >= 0 ? 'positive' : 'negative'}`}>
              {formatCurrency(totalRealized)}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Cost Basis</span>
            <span className="summary-value">{formatCurrency(totalCostBasis)}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Fees</span>
            <span className="summary-value negative">{formatCurrency(totalFees)}</span>
          </div>
          {/* RSI-PAUSE-009: Display paused trade groups count */}
          {pausedCount > 0 && (
            <div className="summary-item paused-count-item">
              <span className="summary-label">Paused Trade Groups</span>
              <span className="summary-value warning">{pausedCount}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ActiveTradeGroupsTable;
